import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Input, Text } from "components";
import AuthActionPage from "components/AuthAction/Index";
import { toast, Toaster } from "sonner";
import { PASSWORD_MIN_LENGTH, resetPassword } from "service/moneeflo";

/**
 * Where the link in the reset email lands.
 *
 * Everyone needs this at least once: passwords did not come across from
 * Xano, so the old one will not work and this is the only way in.
 */
const ResetPasswordPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = () => {
    if (password.length < PASSWORD_MIN_LENGTH) {
      toast.error(`Password must be at least ${PASSWORD_MIN_LENGTH} characters`);
      return;
    }
    if (password !== confirmation) {
      toast.error("Both passwords must match");
      return;
    }

    setLoading(true);
    resetPassword(token, password)
      .then(() => {
        setLoading(false);
        setIsSuccess(true);
        toast.success("Password updated");
        // Long enough to read the confirmation, short enough not to wait.
        setTimeout(() => navigate("/"), 1500);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(
          err?.response?.data?.message ??
            "This reset link is invalid or has expired",
        );
      });
  };

  // A link that arrived without a token cannot be used, so say so rather
  // than showing a form that will always fail.
  if (!token) {
    return (
      <AuthActionPage loading={false}>
        <div className="flex flex-col gap-[23px] items-start justify-start w-full">
          <Text
            className="leading-[25.00px] flex flex-col gap-2 text-xl text-black-900 text-left"
            size="txtInterRegular16"
          >
            <span className="text-black-900_bf font-inter text-left font-medium">
              This link is incomplete
            </span>
            <span className="text-[#707070] font-inter text-base text-left font-normal">
              Request a new password reset email and open the link from there.
            </span>
          </Text>
        </div>
        <div className="flex gap-10 flex-1 sm:flex-col items-end sm:justify-end w-full">
          <Link
            to="/forgetpassword"
            className="bg-blue-700 cursor-pointer py-2.5 rounded-md shadow-bs2 text-base text-center text-white-A700 w-full"
          >
            <Button>Send a new link</Button>
          </Link>
        </div>
        <Toaster theme="dark" position="top-right" />
      </AuthActionPage>
    );
  }

  return (
    <AuthActionPage loading={loading}>
      <div className="flex flex-col gap-[23px] items-start justify-start w-full">
        <Text
          className="leading-[25.00px] flex flex-col gap-2 text-xl text-black-900 text-left"
          size="txtInterRegular16"
        >
          <span className="text-black-900_bf font-inter text-left font-medium">
            {isSuccess ? "Password updated" : "Set a new password"}
          </span>
          <span className="text-[#707070] font-inter text-base text-left font-normal">
            {isSuccess
              ? "You can sign in with your new password now."
              : `Choose a password of at least ${PASSWORD_MIN_LENGTH} characters. This link can only be used once.`}
          </span>
        </Text>
      </div>
      <div className="flex flex-col h-full sm:justify-between items-center justify-start w-full gap-10">
        {!isSuccess && (
          <div className="flex flex-col gap-5 items-start justify-start pt-0.5 w-full">
            <div className="flex flex-col gap-[13px] items-start justify-start w-full">
              <Text className="text-base font-normal text-form-content-label tracking-[0.15px]">
                New password
              </Text>
              <Input
                name="password"
                placeholder="At least 12 characters"
                onChange={(e: string) => setPassword(e)}
                className="font-inter focus-within:border-[#333333] p-0 placeholder:text-inputBorder-base sm:pr-5 text-[15px]text-left border-inputBorder-base w-full"
                wrapClassName="bg-white-A700 focus-within:border-[#333333] border border-black-900_4c border-solid pl-[11px] pr-[35px] py-4 rounded-lg w-full"
                type="password"
              ></Input>
            </div>
            <div className="flex flex-col gap-[13px] items-start justify-start w-full">
              <Text className="text-base font-normal text-form-content-label tracking-[0.15px]">
                Confirm password
              </Text>
              <Input
                name="confirmPassword"
                placeholder="Type it again"
                onChange={(e: string) => setConfirmation(e)}
                className="font-inter focus-within:border-[#333333] p-0 placeholder:text-inputBorder-base sm:pr-5 text-[15px]text-left border-inputBorder-base w-full"
                wrapClassName="bg-white-A700 focus-within:border-[#333333] border border-black-900_4c border-solid pl-[11px] pr-[35px] py-4 rounded-lg w-full"
                type="password"
              ></Input>
            </div>
          </div>
        )}
        <div className="flex gap-10 flex-1 sm:flex-col items-center sm:justify-end w-full">
          <Link
            to="/"
            className=" cursor-pointer py-2.5 rounded-md border text-base text-center text-blue-700 border-blue-700 w-full"
          >
            <Button>Back</Button>
          </Link>
          {isSuccess ? (
            <Link
              to="/"
              className="bg-blue-700 cursor-pointer py-2.5 rounded-md shadow-bs2 text-base text-center text-white-A700 w-full"
            >
              <Button>Sign in</Button>
            </Link>
          ) : (
            <Button
              className="bg-blue-700 cursor-pointer py-2.5 rounded-md shadow-bs2 text-base text-center text-white-A700 w-full"
              onClick={handleSubmit}
            >
              Set password
            </Button>
          )}
        </div>
      </div>
      <Toaster theme="dark" position="top-right" />
    </AuthActionPage>
  );
};

export default ResetPasswordPage;
